import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Plus, X, Save } from "lucide-react";
import { adminGetTrust, adminUpdateTrust } from "@/lib/api";
import { ImageUpload, StringListInput } from "@/admin/FormFields";

export default function TrustEditor() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["trust-admin"], queryFn: adminGetTrust });
  const [form, setForm] = useState(null);
  useEffect(() => { if (data) setForm(data); }, [data, setForm]);

  const mut = useMutation({
    mutationFn: (d) => adminUpdateTrust(d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["trust"] }); toast.success("Trust details saved"); },
    onError: () => toast.error("Save failed"),
  });

  if (isLoading || !form) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inp = "w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none text-sm";

  const setTrustee = (i, k, v) => setForm((f) => {
    const arr = [...(f.trustees || [])]; arr[i] = { ...arr[i], [k]: v }; return { ...f, trustees: arr };
  });
  const setImpact = (i, k, v) => setForm((f) => {
    const arr = [...(f.impact || [])]; arr[i] = { ...arr[i], [k]: v }; return { ...f, impact: arr };
  });

  return (
    <div data-testid="trust-editor">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-2xl md:text-3xl font-semibold text-navy">Charitable Trust</h1>
        <button onClick={() => mut.mutate(form)} disabled={mut.isPending} data-testid="save-trust"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gold text-navy font-semibold text-sm disabled:opacity-60">
          {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Changes
        </button>
      </div>

      <div className="space-y-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
          <Row label="Trust Name"><input value={form.name || ""} onChange={(e) => set("name", e.target.value)} className={inp} /></Row>
          <Row label="Mission"><textarea rows={2} value={form.mission || ""} onChange={(e) => set("mission", e.target.value)} className={`${inp} resize-none`} /></Row>
          <Row label="Vision"><textarea rows={2} value={form.vision || ""} onChange={(e) => set("vision", e.target.value)} className={`${inp} resize-none`} /></Row>
          <Row label="Registration Details"><input value={form.registration || ""} onChange={(e) => set("registration", e.target.value)} className={inp} /></Row>
          <Row label="Image"><ImageUpload value={form.image} onChange={(v) => set("image", v)} /></Row>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="font-display text-lg font-semibold text-navy mb-4">Key Activities</h2>
          <StringListInput value={form.activities} onChange={(v) => set("activities", v)} />
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-3">
          <h2 className="font-display text-lg font-semibold text-navy mb-2">Impact Numbers</h2>
          {(form.impact || []).map((im, i) => (
            <div key={i} className="grid grid-cols-12 gap-3 items-end">
              <div className="col-span-7"><label className="text-xs text-slate-400">Label</label><input value={im.label} onChange={(e) => setImpact(i, "label", e.target.value)} className={inp} /></div>
              <div className="col-span-4"><label className="text-xs text-slate-400">Value</label><input value={im.value} onChange={(e) => setImpact(i, "value", e.target.value)} className={inp} /></div>
              <button onClick={() => set("impact", form.impact.filter((_, idx) => idx !== i))} className="col-span-1 h-9 w-9 rounded-lg bg-red-50 text-red-500 flex items-center justify-center"><X className="h-4 w-4" /></button>
            </div>
          ))}
          <button onClick={() => set("impact", [...(form.impact || []), { label: "", value: "" }])} className="inline-flex items-center gap-1.5 text-sm text-navy font-medium"><Plus className="h-4 w-4" /> Add impact</button>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-3">
          <h2 className="font-display text-lg font-semibold text-navy mb-2">Trustees & Team</h2>
          {(form.trustees || []).map((tr, i) => (
            <div key={i} className="grid grid-cols-12 gap-3 items-end">
              <div className="col-span-6"><label className="text-xs text-slate-400">Name</label><input value={tr.name} onChange={(e) => setTrustee(i, "name", e.target.value)} className={inp} /></div>
              <div className="col-span-5"><label className="text-xs text-slate-400">Role</label><input value={tr.role} onChange={(e) => setTrustee(i, "role", e.target.value)} className={inp} /></div>
              <button onClick={() => set("trustees", form.trustees.filter((_, idx) => idx !== i))} className="col-span-1 h-9 w-9 rounded-lg bg-red-50 text-red-500 flex items-center justify-center"><X className="h-4 w-4" /></button>
            </div>
          ))}
          <button onClick={() => set("trustees", [...(form.trustees || []), { name: "", role: "" }])} className="inline-flex items-center gap-1.5 text-sm text-navy font-medium"><Plus className="h-4 w-4" /> Add trustee</button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-navy mb-1.5">{label}</label>
      {children}
    </div>
  );
}
